import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    TextField, MenuItem, Button, Box, Typography, IconButton, CircularProgress, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
// The v4 Button, aliased, for the Refresh control only.
//
// This page's other Buttons come from @mui/material (v5), whose emotion styles
// are injected AFTER v4's JSS - so .MuiButton-containedPrimary beat the
// makeStyles class and Refresh rendered blue with white text instead of white
// with orange text. MarketingJunk looks right precisely because its Button is
// the v4 one, where the class wins.
//
// Aliased rather than swapping the shared import: the Delete button below uses
// color="error", which only exists on the v5 Button.
import V4Button from '@material-ui/core/Button';
// Material-UI icons - the Ant Design set these replace was the odd one out
// against every other page in the app, and read as dated beside them.
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import SwapHorizOutlinedIcon from "@material-ui/icons/SwapHorizOutlined";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ArrowForwardOutlinedIcon from "@material-ui/icons/ArrowForwardOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import LinearProgress from "@material-ui/core/LinearProgress";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";

// Brand orange - sampled from the actual Decorpot logo, same palette used
// across the rest of the app's recently-redesigned pages.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
// Matches the "Silka bold" heading treatment used on the lead details
// (manageProjectLog) page, so headings here read consistently with the rest
// of the app. Body text already inherits "Silka regular" globally via App.css.
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

// Indeterminate bar shown while the table is being fetched. Same treatment as
// the other orange admin pages, so they read as one system.
const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles(() => ({
    page: { padding: 24, background: "#f6f7fb", minHeight: "100vh" },
    headerCard: {
        marginBottom: 20,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
        background: HEADER_GRADIENT,
        padding: "24px 28px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 16 },
    headerIconWrap: {
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 10,
        flexWrap: "wrap",
    },
    // Identical to the refresh button on Experience Centers, Locations,
    // Departments, Teams and the Marketing pages - same radius, padding, ring
    // and hover - so the control reads the same everywhere it appears.
    refreshBtn: {
        background: "#fff",
        color: ORANGE_DARK,
        fontWeight: 700,
        borderRadius: 10,
        textTransform: "none",
        padding: "9px 18px",
        border: `1px solid ${ORANGE_DARK}`,
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
        "&:hover": { background: ORANGE_LIGHT },
        "&.Mui-disabled": { background: "rgba(255,255,255,0.75)", color: ORANGE_DARK },
    },
    spin: { animation: "$spin 0.9s linear infinite" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    lastUpdatedText: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: "rgba(255,255,255,0.92)",
    },
    liveDot: {
        width: 7, height: 7, borderRadius: "50%",
        background: "#8ef5b5", boxShadow: "0 0 0 3px rgba(142,245,181,0.25)",
    },
    formLoadingNote: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 7,
        marginBottom: 2,
        fontSize: 12.5,
        fontWeight: 600,
        color: ORANGE_DARK,
    },
    progressSlot: { height: 4, marginBottom: 10 },
    loadingBanner: {
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 14px", marginBottom: 14, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: "#7a6a60", marginTop: 1 },
    // From -> To reads as one journey rather than two unrelated columns.
    ecChip: {
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 11px", borderRadius: 999,
        background: ORANGE_LIGHT, color: ORANGE_DARK,
        fontSize: 12.5, fontWeight: 700, maxWidth: 230,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    statChip: {
        background: "rgba(255,255,255,0.2)",
        color: "#fff",
        borderRadius: 20,
        padding: "7px 16px",
        fontWeight: 700,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    formCard: {
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 6px 20px rgba(17,24,39,0.06)",
        padding: "22px 24px",
        marginBottom: 20,
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        flexWrap: "wrap",
    },
    select: {
        minWidth: 260,
        flex: "1 1 260px",
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    swapBtn: {
        marginTop: 6,
        color: ORANGE_DARK,
        background: ORANGE_LIGHT,
        '&:hover': { background: "#ffe3d2" },
    },
    submitBtn: {
        marginTop: 4,
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        padding: "9px 22px",
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "none",
        '&:hover': { background: `linear-gradient(135deg, #a8441a, ${ORANGE_DARK})`, boxShadow: "0 6px 16px rgba(244,126,68,0.35)" },
        '&.Mui-disabled': { background: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.38)" },
    },
    tableCard: { borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)", background: "#fff" },
    tableWrap: { maxHeight: 560, overflow: "auto" },
    emptyState: { padding: "60px 20px", textAlign: "center", color: "#9aa1ac" },
    emptyIconWrap: {
        width: 76,
        height: 76,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    deleteBtn: {
        borderRadius: 8,
        textTransform: "none",
        minWidth: 92,
    },
}));

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: ORANGE_DARK,
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        '&:nth-of-type(odd)': { backgroundColor: "#fafafa" },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
        transition: "background-color .12s ease",
    },
}))(TableRow);

const RedirectEC = (props) => {

    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const [commonExpCenter, setExpCenter] = useState([]);
    const [selectedExperienceCenterFrom, setSelectedExperienceCenterFrom] = useState("");
    const [selectedExperienceCenterTo, setSelectedExperienceCenterTo] = useState("");
    const [redirectECData, setRedirectECData] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    // Item pending delete confirmation - null when the dialog is closed.
    const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
    // The redirection table's own fetch. It previously had NO loading state at
    // all - the table simply popped in - so there was nothing to drive a bar,
    // a skeleton or a Refresh button.
    const [dataLoading, setDataLoading] = useState(false);
    // The Experience Centre dropdowns. Scoped so loading them no longer puts the
    // whole page behind the app-wide Backdrop.
    const [ecLoading, setEcLoading] = useState(false);
    // When the rows on screen were last pulled. Only set on a SUCCESSFUL fetch,
    // so a failed refresh never makes stale rows look fresh.
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        expCenters();
        fetchRedirectEC();
        // eslint-disable-next-line
    }, []);

    const expCenters = async () => {
        setEcLoading(true);
        try {
            let response = await HttpService.getExpericenCenters();
            const matchedUsers = response.data.filter(user =>
                userDetails.experienceCenterId.includes(user._id)
            );
            setExpCenter(matchedUsers);
        } catch (error) {
            console.error("Error fetching experience centers:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
        }
        setEcLoading(false);
    };

    const fetchRedirectEC = async () => {
        setDataLoading(true);
        try {
            const response = await HttpService.getRedirectEC();
            setRedirectECData(response.data);
            // Inside the try, after the assignment: a fetch that threw leaves
            // the previous timestamp alone rather than vouching for stale data.
            setLastUpdated(moment());
        } catch (error) {
            console.error("fetchEC ->", error);
        }
        setDataLoading(false);
    };

    const isSameSelection = selectedExperienceCenterFrom && selectedExperienceCenterTo &&
        selectedExperienceCenterFrom === selectedExperienceCenterTo;
    const isSubmitDisabled = !selectedExperienceCenterFrom || !selectedExperienceCenterTo || isSameSelection || submitting;

    const handleSwap = () => {
        setSelectedExperienceCenterFrom(selectedExperienceCenterTo);
        setSelectedExperienceCenterTo(selectedExperienceCenterFrom);
    };

    // Local button-level spinner instead of the app-wide Backdrop, so
    // submitting gives immediate, visible feedback right on the button.
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            let obj = {
                createdBy: userDetails.name,
                redirectECFrom: selectedExperienceCenterFrom,
                redirectECTo: selectedExperienceCenterTo
            };

            await HttpService.redirectEC(obj);
            props.setSnackInfo("Redirection created successfully", "success");
            setSelectedExperienceCenterFrom("");
            setSelectedExperienceCenterTo("");
            fetchRedirectEC();
        } catch (error) {
            console.error("Error ->", error);
            props.setSnackInfo(error?.response?.data?.message || "Something went wrong", "error");
        }
        setSubmitting(false);
    };

    const handleDelete = async (item) => {
        setDeletingId(item._id);
        try {
            await HttpService.deleteRedirectEC(item);
            props.setSnackInfo("Redirection removed successfully", "success");
            fetchRedirectEC();
        } catch (error) {
            console.error("Delete error ->", error);
            props.setSnackInfo(error?.response?.data?.message || "Something went wrong", "error");
        }
        setDeletingId(null);
        setConfirmDeleteItem(null);
    };

    return (
        <div className={classes.page}>
            {/* HEADER */}
            <Box className={classes.headerCard}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconWrap}>
                        <SwapHorizOutlinedIcon style={{ color: "#fff", fontSize: 26 }} />
                    </Box>
                    <Box>
                        <Typography style={{ color: "#fff", fontFamily: HEADING_FONT, fontSize: 22, margin: 0 }}>
                            Redirect Experience Center
                        </Typography>
                        <Typography style={{ color: "rgba(255,255,255,0.88)", fontSize: 13.5, marginTop: 4 }}>
                            Route incoming leads from one Experience Center to another
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.headerActions}>
                    <Box className={classes.statChip}>
                        <AccountBalanceOutlinedIcon style={{ fontSize: 15 }} />
                        {redirectECData?.length || 0} Active Redirection{redirectECData?.length === 1 ? "" : "s"}
                    </Box>

                    <Tooltip
                        title={
                            lastUpdated
                                ? `Last loaded at ${lastUpdated.format("DD MMM YYYY, hh:mm:ss A")} — click to reload`
                                : "Reload the redirections"
                        }
                    >
                        {/* A disabled button fires no mouse events, so the tooltip
                            would die exactly when it is most wanted - mid-refresh. */}
                        <span style={{ display: "inline-flex" }}>
                            {/* V4Button, exactly as MarketingJunk does it - the
                                makeStyles class only wins against the v4
                                component. See the import for why. */}
                            <V4Button
                                className={classes.refreshBtn}
                                disabled={dataLoading}
                                onClick={fetchRedirectEC}
                                startIcon={<RefreshIcon className={dataLoading ? classes.spin : ""} />}
                            >
                                {dataLoading ? "Refreshing…" : "Refresh"}
                            </V4Button>
                        </span>
                    </Tooltip>

                    {lastUpdated && (
                        <Box className={classes.lastUpdatedText}>
                            <span className={classes.liveDot} />
                            {dataLoading ? "Loading…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Reserved slot rather than a conditional element, so starting a
                fetch does not jog the page down by 4px and back again. */}
            <Box className={classes.progressSlot}>
                {dataLoading ? <BrandLinearProgress /> : null}
            </Box>

            {/* The bar alone says "something is happening"; this says WHAT,
                which matters most on a refresh. */}
            {dataLoading && (
                <Box className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <Box style={{ minWidth: 0 }}>
                        <Box className={classes.loadingBannerTitle}>Fetching redirections…</Box>
                        <Box className={classes.loadingBannerHint}>
                            {redirectECData?.length
                                ? "Reloading — the redirections below stay visible until the new data lands."
                                : "Pulling every active Experience Center redirection."}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* CREATE FORM */}
            <Box className={classes.formCard}>
                {/* Said ONCE for the pair. It used to be a helperText on each
                    field, which printed the same sentence twice and - because
                    the To field already had a validation helperText - collided
                    with it. */}
                {ecLoading && (
                    <Box className={classes.formLoadingNote}>
                        <CircularProgress size={13} thickness={5} style={{ color: ORANGE_DARK }} />
                        Loading Experience Centers…
                    </Box>
                )}
                <TextField
                    select
                    id="redirect-from-ec"
                    value={selectedExperienceCenterFrom}
                    label="Redirect From EC"
                    name="redirectFromEC"
                    onChange={(e) => setSelectedExperienceCenterFrom(e.target.value)}
                    variant="outlined"
                    size="small"
                    // Disabled until the centres arrive, so the field cannot be
                    // opened onto an empty list and look broken. The reason is
                    // stated once below the pair, not on each field.
                    disabled={ecLoading}
                    className={classes.select}
                >
                    {commonExpCenter.map((center) => (
                        <MenuItem key={center._id} value={center.name}>
                            {center.name}
                        </MenuItem>
                    ))}
                </TextField>

                <Tooltip title="Swap From / To">
                    <span>
                        <IconButton
                            className={classes.swapBtn}
                            onClick={handleSwap}
                            disabled={!selectedExperienceCenterFrom && !selectedExperienceCenterTo}
                        >
                            <SwapHorizOutlinedIcon />
                        </IconButton>
                    </span>
                </Tooltip>

                <TextField
                    select
                    id="redirect-to-ec"
                    value={selectedExperienceCenterTo}
                    label="Redirect To EC"
                    name="redirectToEC"
                    onChange={(e) => setSelectedExperienceCenterTo(e.target.value)}
                    variant="outlined"
                    size="small"
                    // Disabled until the centres arrive, so the field cannot be
                    // opened onto an empty list and look broken.
                    disabled={ecLoading}
                    className={classes.select}
                    error={isSameSelection}
                    // ONE helperText. This field briefly had two - a loading one
                    // and this validation one - and in JSX the last prop wins, so
                    // the loading message silently never rendered here.
                    helperText={isSameSelection ? "From and To must be different" : " "}
                >
                    {commonExpCenter.map((center) => (
                        <MenuItem key={center._id} value={center.name}>
                            {center.name}
                        </MenuItem>
                    ))}
                </TextField>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                    className={classes.submitBtn}
                    startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendOutlinedIcon />}
                >
                    {submitting ? "Submitting..." : "Submit"}
                </Button>
            </Box>

            {/* TABLE */}
            <Box className={classes.tableCard}>
                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>SL No.</StyledTableCell>
                                <StyledTableCell>Redirected From</StyledTableCell>
                                <StyledTableCell>Redirected To</StyledTableCell>
                                <StyledTableCell>Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dataLoading && !redirectECData?.length ? (
                                // Placeholder rows shaped like real ones, so the
                                // card keeps its height instead of collapsing and
                                // snapping back when the data lands. A REFRESH
                                // (rows already present) keeps them on screen.
                                Array.from({ length: 4 }).map((_, i) => (
                                    <StyledTableRow key={`sk-${i}`}>
                                        <StyledTableCell><Skeleton variant="text" width={18} /></StyledTableCell>
                                        <StyledTableCell><Skeleton variant="rect" width={150} height={26} style={{ borderRadius: 999 }} /></StyledTableCell>
                                        <StyledTableCell><Skeleton variant="rect" width={150} height={26} style={{ borderRadius: 999 }} /></StyledTableCell>
                                        <StyledTableCell><Skeleton variant="rect" width={92} height={30} style={{ borderRadius: 8 }} /></StyledTableCell>
                                    </StyledTableRow>
                                ))
                            ) : (!redirectECData || redirectECData.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={4} style={{ border: "none" }}>
                                        <Box className={classes.emptyState}>
                                            <Box className={classes.emptyIconWrap}>
                                                <InboxOutlinedIcon style={{ fontSize: 32, color: ORANGE_DARK }} />
                                            </Box>
                                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                                No redirections set up yet
                                            </Typography>
                                            <Typography variant="body2" style={{ color: "#a4abb5", marginTop: 4 }}>
                                                Choose a "From" and "To" Experience Center above and click Submit.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                redirectECData.map((item, index) => (
                                    <StyledTableRow key={item._id || index}>
                                        <StyledTableCell>{index + 1}</StyledTableCell>
                                        <StyledTableCell>
                                            <Tooltip title={item.redirectECFrom || ""}>
                                                <span className={classes.ecChip}>
                                                    <StorefrontOutlinedIcon style={{ fontSize: 14 }} />
                                                    {item.redirectECFrom}
                                                </span>
                                            </Tooltip>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {/* The arrow makes the row read as one
                                                journey rather than two unrelated
                                                centre names side by side. */}
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                <ArrowForwardOutlinedIcon style={{ fontSize: 16, color: "#b9c0cc" }} />
                                                <Tooltip title={item.redirectECTo || ""}>
                                                    <span className={classes.ecChip}>
                                                        <StorefrontOutlinedIcon style={{ fontSize: 14 }} />
                                                        {item.redirectECTo}
                                                    </span>
                                                </Tooltip>
                                            </span>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => setConfirmDeleteItem(item)}
                                                size="small"
                                                disabled={deletingId === item._id}
                                                className={classes.deleteBtn}
                                                startIcon={deletingId === item._id ? <CircularProgress size={14} color="error" /> : <DeleteOutlineIcon />}
                                            >
                                                {deletingId === item._id ? "Removing..." : "Delete"}
                                            </Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* DELETE CONFIRMATION */}
            <Dialog
                open={!!confirmDeleteItem}
                onClose={() => !deletingId && setConfirmDeleteItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 14 } }}
            >
                <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <ReportProblemOutlinedIcon style={{ color: "#d32f2f", fontSize: 22 }} />
                    Remove this redirection?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ color: "#5a6b78" }}>
                        This will stop redirecting leads from{" "}
                        <b>{confirmDeleteItem?.redirectECFrom}</b> to{" "}
                        <b>{confirmDeleteItem?.redirectECTo}</b>. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: "12px 24px 20px" }}>
                    <Button
                        onClick={() => setConfirmDeleteItem(null)}
                        disabled={!!deletingId}
                        style={{ color: "#6b7280" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleDelete(confirmDeleteItem)}
                        variant="contained"
                        color="error"
                        disabled={!!deletingId}
                        startIcon={deletingId === confirmDeleteItem?._id ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteOutlineIcon />}
                    >
                        {deletingId === confirmDeleteItem?._id ? "Removing..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default connect(null, { setSnackInfo })(RedirectEC);